import { inject, Injectable } from '@angular/core';
import {
  BarService,
  FrontDeskService,
  HousekeepingService,
  KioskService,
  LocationsService,
  RoomsService,
  RoomStatusService,
  StaffService,
} from '@era/locations-sdk';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { CategoryType } from '@rocksoft/shared';
import { compare } from 'fast-json-patch';
import { EMPTY, Observable, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { safeMap } from '../effects.helpers';
import {
  CategoryApiActions,
  CategoryGeoLocationsActions,
  CategoryListActions,
  CategoryResolverActions,
  CategoryStatusContainerActions,
} from './category.actions';
import { CategoriesUnion, categoryEntityFrom } from './category.state';

@Injectable()
export class CategoryEffects {
  private readonly actions$ = inject(Actions);
  private readonly barService = inject(BarService);
  private readonly frontDeskService = inject(FrontDeskService);
  private readonly housekeepingService = inject(HousekeepingService);
  private readonly kioskService = inject(KioskService);
  private readonly locationService = inject(LocationsService);
  private readonly roomsService = inject(RoomsService);
  private readonly roomStatusService = inject(RoomStatusService);
  private readonly staffService = inject(StaffService);

  categoriesLoadedEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CategoryResolverActions.categoriesRequested),
      mergeMap((action) =>
        safeMap(
          this.loadCategories(action.categoryType, action.locationId),
          (configurations) => CategoryApiActions.categoriesLoaded({ configurations }),
          (error) => CategoryApiActions.categoriesLoadedError({ error })
        )
      )
    );
  });

  categoryAddedEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CategoryListActions.categoryAdded),
      mergeMap((action) =>
        safeMap(
          this.addCategory(action.entity),
          (configuration) =>
            CategoryApiActions.categoryAdded({
              configuration,
              originalConfiguration: action.entity,
            }),
          (error) => CategoryApiActions.categoryAddedError({ configuration: action.entity, error })
        )
      )
    );
  });

  categoryUpdatedEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CategoryListActions.categoryUpdated),
      mergeMap((action) =>
        safeMap(
          this.updateCategory(action.originalEntity, action.entity),
          (configuration) => CategoryApiActions.categoryUpdated({ configuration }),
          (error) =>
            CategoryApiActions.categoryUpdatedError({
              originalConfiguration: action.originalEntity,
              configuration: action.entity,
              error,
            })
        )
      )
    );
  });

  categoryDeletedEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CategoryListActions.categoryDeleted),
      mergeMap((action) =>
        safeMap(
          this.deleteCategory(action.entity),
          (configuration) => CategoryApiActions.categoryDeleted({ configuration }),
          (error) => CategoryApiActions.categoryDeletedError({ configuration: action.entity, error })
        )
      )
    );
  });

  categoryListUpdatedEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CategoryListActions.categoryUpdated),
      mergeMap((action) =>
        safeMap(
          this.updateCategory(action.originalEntity, action.entity),
          (configuration) => CategoryApiActions.categoryUpdated({ configuration }),
          (error) =>
            CategoryApiActions.categoryUpdatedError({
              originalConfiguration: action.originalEntity,
              configuration: action.entity,
              error,
            })
        )
      )
    );
  });

  categoryGeoLocationsUpdatedEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CategoryGeoLocationsActions.categoryUpdated),
      mergeMap((action) =>
        safeMap(
          this.updateCategory(action.originalEntity, action.entity),
          (configuration) => CategoryApiActions.categoryUpdated({ configuration }),
          (error) =>
            CategoryApiActions.categoryUpdatedError({
              originalConfiguration: action.originalEntity,
              configuration: action.entity,
              error,
            })
        )
      )
    );
  });

  categoryStatusChangedEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CategoryStatusContainerActions.statusChanged),
      mergeMap((action) => {
        const serviceCall = action.status
          ? this.roomStatusService.checkIn({ locationId: action.room.locationId, roomHumanId: action.room.id })
          : this.roomStatusService.checkOut({ locationId: action.room.locationId, roomHumanId: action.room.id });

        return safeMap(
          serviceCall,
          () =>
            CategoryApiActions.statusChanged({
              categoryType: 'Room',
              categoryId: action.room.uniqueId,
              status: action.status,
            }),
          (error) =>
            CategoryApiActions.statusChangedError({ categoryType: 'Room', categoryId: action.room.uniqueId, error })
        );
      })
    );
  });

  private loadCategories(categoryType: CategoryType, locationId?: string): Observable<CategoriesUnion[]> {
    switch (categoryType) {
      case 'Location':
        return this.locationService
          .getLocations()
          .pipe(map((locations) => locations.map((location) => categoryEntityFrom('Location', location))));
      case 'Room':
        if (!locationId) {
          return EMPTY;
        }
        return this.roomsService
          .getRooms({ locationId })
          .pipe(map((rooms) => rooms.map((room) => categoryEntityFrom('Room', room))));
      case 'Bar':
        if (!locationId) return EMPTY;
        return this.barService.getBar({ locationId }).pipe(map((bar) => [categoryEntityFrom('Bar', bar)]));
      case 'FrontDesk':
        if (!locationId) return EMPTY;
        return this.frontDeskService
          .getFrontDesk({ locationId })
          .pipe(map((frontDesk) => [categoryEntityFrom('FrontDesk', frontDesk)]));
      case 'Housekeeping':
        if (!locationId) return EMPTY;
        return this.housekeepingService
          .getHousekeeping({ locationId })
          .pipe(map((housekeeping) => [categoryEntityFrom('Housekeeping', housekeeping)]));
      case 'Kiosk':
        if (!locationId) return EMPTY;
        return this.kioskService.getKiosk({ locationId }).pipe(map((kiosk) => [categoryEntityFrom('Kiosk', kiosk)]));
      case 'Staff':
        if (!locationId) return EMPTY;
        return this.staffService.getStaff({ locationId }).pipe(map((staff) => [categoryEntityFrom('Staff', staff)]));
      case 'Brand':
        return of([
          categoryEntityFrom('Brand', { id: 'default-brand', displayName: 'Default Brand', uniqueId: 'default-brand' }),
        ]);
      default:
        return EMPTY;
    }
  }

  private addCategory(entity: CategoriesUnion): Observable<CategoriesUnion> {
    switch (entity.categoryType) {
      case 'Location': {
        const location = entity.entity;
        if (!location) return EMPTY;
        const mutateLocation = {
          displayName: location.displayName,
          id: location.id,
        };
        return this.locationService
          .addLocation({ mutateLocation })
          .pipe(map((addedLocation) => categoryEntityFrom('Location', addedLocation)));
      }
      case 'Room': {
        const room = entity.entity;
        if (!room) return EMPTY;
        return this.roomsService
          .addRoom({ locationId: room.locationId, mutateRoom: room })
          .pipe(map((addedRoom) => categoryEntityFrom('Room', addedRoom)));
      }
      default:
        return EMPTY;
    }
  }

  private updateCategory(originalEntity: CategoriesUnion, entity: CategoriesUnion): Observable<CategoriesUnion> {
    switch (entity.categoryType) {
      case 'Location': {
        const originalLocation = originalEntity.entity;
        const location = entity.entity;
        if (!originalLocation || !location) return EMPTY;
        const update = compare(originalLocation, location);
        return this.locationService
          .updateLocation({ locationId: originalLocation.id, operation: update })
          .pipe(map(() => categoryEntityFrom('Location', location)));
      }
      case 'Room': {
        const room = entity.entity;
        const originalRoom = originalEntity.entity;
        if (!room || !originalRoom) return EMPTY;
        const roomUpdate = compare(originalRoom, room);
        return this.roomsService
          .updateRoom({ locationId: room.locationId, roomId: room.id, operation: roomUpdate })
          .pipe(map(() => categoryEntityFrom('Room', room)));
      }
      default:
        return EMPTY;
    }
  }

  private deleteCategory(entity: CategoriesUnion): Observable<CategoriesUnion> {
    switch (entity.categoryType) {
      case 'Location': {
        const location = entity.entity;
        if (!location) return EMPTY;
        return this.locationService
          .deleteLocation({ locationId: location.id })
          .pipe(map(() => categoryEntityFrom('Location', location)));
      }
      case 'Room': {
        const room = entity.entity;
        if (!room) return EMPTY;
        return this.roomsService
          .deleteRoom({ locationId: room.locationId, roomId: room.id })
          .pipe(map(() => categoryEntityFrom('Room', room)));
      }
      default:
        return EMPTY;
    }
  }
}
